import React from "react";
import { Card, H1, H3, Tag, Spinner, NonIdealState, Button } from "@blueprintjs/core";
import { inject, observer } from "mobx-react";
import { apiCallGet } from "../fns/util";
import cogoToast from "cogo-toast";
import { withRouter } from "react-router";
import { indianRupee, niceDateTime } from "../fns/util";

class FinalInvoicePage extends React.Component {
  state = {
    isLoading: true,
    booking: null,
    finalInvoice: null,
    authorization: null,
    error: null,
  };

  componentDidMount() {
    this.loadInvoiceData();
  }

  loadInvoiceData = async () => {
    try {
      const sxId = this.props.match.params.sxId;
      if (!sxId) {
        this.setState({
          error: "Surgery ID not provided",
          isLoading: false,
        });
        return;
      }

      console.log("🔄 Loading final invoice data for surgery:", sxId);
      
      // Load booking data with all invoice information
      const response = await apiCallGet(
        `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/getByIdForBill/${sxId}`
      );

      console.log("📄 Final invoice data response:", response);

      if (response && response.success && response.data) {
        const booking = response.data;
        
        // Extract surgery bills from selectedSurgeries
        const surgeryBills = (booking.selectedSurgeries || []).map((surgery, index) => ({
          header: {
            printedName: surgery.name || `Surgery ${index + 1}`,
            group: surgery.group || "General"
          },
          surgeryRatePaise: (surgery.pricing?.surgeryRate || 0) * 100,
          anaestheticChargesPaise: (surgery.pricing?.anaestheticCharges || 0) * 100,
          tissueProcessingPaise: (surgery.pricing?.tissueProcessing || 0) * 100,
          procedureCostPaise: (surgery.pricing?.procedureCost || 0) * 100,
          implantCostInclusivePaise: (surgery.pricing?.implantCost || 0) * 100,
          femtoChargesPaise: (surgery.pricing?.femto || 0) * 100,
          specialDiscountPaise: (surgery.discounts?.specialDiscount || 0) * 100,
          iolDiscountPaise: (surgery.discounts?.iolDiscount || 0) * 100,
          mouDiscountPaise: (surgery.discounts?.mouDiscount || 0) * 100,
          mouDiscountPercent: surgery.discounts?.mouDiscount || 0,
          taxProc: {
            cgst: surgery.pricing?.taxApplicable ? surgery.pricing.taxApplicable / 2 : 0,
            sgst: surgery.pricing?.taxApplicable ? surgery.pricing.taxApplicable / 2 : 0,
            absolutePaise: ((surgery.pricing?.procedureCost || 0) * (surgery.pricing?.taxApplicable || 0) / 100) * 100
          },
          taxImplant: {
            cgst: surgery.pricing?.implantTax ? surgery.pricing.implantTax / 2 : 0,
            sgst: surgery.pricing?.implantTax ? surgery.pricing.implantTax / 2 : 0,
            absolutePaise: ((surgery.pricing?.implantCost || 0) * (surgery.pricing?.implantTax || 0) / 100) * 100
          },
          subtotalPaise: booking.surgerySubtotal ? booking.surgerySubtotal * 100 : 0,
          manualRows: []
        }));

        // Calculate overall total
        const overallPaise = surgeryBills.reduce((sum, bill) => sum + (bill.subtotalPaise || 0), 0);

        this.setState({
          booking,
          finalInvoice: {
            surgeryBillsSnapshot: surgeryBills,
            overallPaise
          },
          authorization: booking.authorization || null,
          isLoading: false,
        });
      } else {
        throw new Error("Invalid response format");
      }
    } catch (err) {
      console.error("❌ Error loading final invoice data:", err);
      this.setState({
        error: "Failed to load final invoice data",
        isLoading: false,
      });
      cogoToast.error("Failed to load final invoice data");
    }
  };

  renderInvoiceHeader = () => {
    const { booking, authorization } = this.state;
    const auth = authorization?.entries?.[0] || authorization || {};
    
    return (
      <Card style={{ marginBottom: "20px", backgroundColor: "var(--bg-surface)" }}>
        <H3 style={{ color: "var(--text-primary)", margin: 0 }}>Invoice Header</H3>
        <div style={{ marginTop: "15px" }}>
          <div style={{ marginBottom: "10px" }}>
            <Tag large minimal>Patient: {booking.patientName || "NA"}</Tag>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <Tag large minimal>PID: {booking.patientId || "NA"}</Tag>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <Tag large minimal>Phone: {booking.patientPhone || "NA"}</Tag>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <Tag large minimal>Doctor: {booking.doctorName || "NA"}</Tag>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <Tag large minimal>Branch: {booking.branchName || "NA"}</Tag>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <Tag large minimal>Date: {booking.scheduledDate ? new Date(booking.scheduledDate).toLocaleDateString() : "NA"}</Tag>
          </div>
          {auth.tpaName && (
            <div style={{ marginBottom: "10px" }}>
              <Tag large minimal>TPA: {auth.tpaName || "NA"}</Tag>
            </div>
          )}
          {auth.insuranceName && (
            <div>
              <Tag large minimal>Insurer: {auth.insuranceName || "NA"}</Tag>
            </div>
          )}
        </div>
      </Card>
    );
  };

  renderSurgeryBill = (bill, index) => {
    return (
      <Card key={index} style={{ marginBottom: "15px", backgroundColor: "var(--bg-surface)" }}>
        <H3 style={{ color: "var(--text-primary)", margin: 0, marginBottom: "15px" }}>
          Surgery {index + 1}: {bill.header?.printedName || "-"}
        </H3>
        
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
          <div>
            <Tag large minimal>Surgery Rate: {indianRupee(bill.surgeryRatePaise || 0)}</Tag>
          </div>
          <div>
            <Tag large minimal>Anaesthetic: {indianRupee(bill.anaestheticChargesPaise || 0)}</Tag>
          </div>
          <div>
            <Tag large minimal>Tissue Processing: {indianRupee(bill.tissueProcessingPaise || 0)}</Tag>
          </div>
          <div>
            <Tag large minimal>Procedure Cost: {indianRupee(bill.procedureCostPaise || 0)}</Tag>
          </div>
          <div>
            <Tag large minimal>Implant Cost: {indianRupee(bill.implantCostInclusivePaise || 0)}</Tag>
          </div>
          <div>
            <Tag large minimal>Femto Charges: {indianRupee(bill.femtoChargesPaise || 0)}</Tag>
          </div>
          <div>
            <Tag large minimal intent="warning">Special Discount: {indianRupee(bill.specialDiscountPaise || 0)}</Tag>
          </div>
          <div>
            <Tag large minimal intent="warning">IOL Discount: {indianRupee(bill.iolDiscountPaise || 0)}</Tag>
          </div>
          <div>
            <Tag large minimal intent="warning">
              MoU Discount: {indianRupee(bill.mouDiscountPaise || 0)} ({bill.mouDiscountPercent || 0}%)
            </Tag>
          </div>
          <div>
            <Tag large minimal>Proc Tax: {indianRupee(bill.taxProc?.absolutePaise || 0)}</Tag>
          </div>
          <div>
            <Tag large minimal>Implant Tax: {indianRupee(bill.taxImplant?.absolutePaise || 0)}</Tag>
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <Tag large minimal intent="success" style={{ fontSize: "16px" }}>
              Subtotal: {indianRupee(bill.subtotalPaise || 0)}
            </Tag>
          </div>
        </div>
      </Card>
    );
  };

  renderOverallTotals = () => {
    const { finalInvoice } = this.state;
    
    return (
      <Card style={{ marginBottom: "20px", backgroundColor: "var(--bg-surface)" }}>
        <H3 style={{ color: "var(--text-primary)", margin: 0 }}>Overall Totals</H3>
        <div style={{ marginTop: "15px" }}>
          <Tag large minimal intent="success" style={{ fontSize: "18px" }}>
            Grand Total: {indianRupee(finalInvoice.overallPaise || 0)}
          </Tag>
        </div>
      </Card>
    );
  };

  render() {
    if (this.state.isLoading) {
      return (
        <div style={{ 
          padding: "20px", 
          backgroundColor: "var(--bg-primary)", 
          color: "var(--text-primary)", 
          minHeight: "100vh" 
        }}>
          <NonIdealState
            icon={<Spinner />}
            title="Loading Final Invoice..."
            description="Please wait while we prepare the final invoice."
          />
        </div>
      );
    }

    if (this.state.error) {
      return (
        <div style={{ 
          padding: "20px", 
          backgroundColor: "var(--bg-primary)", 
          color: "var(--text-primary)", 
          minHeight: "100vh" 
        }}>
          <NonIdealState
            icon="error"
            title="Error Loading Invoice"
            description={this.state.error}
            action={
              <Button 
                intent="primary" 
                icon="refresh" 
                onClick={this.loadInvoiceData}
              >
                Retry
              </Button>
            }
          />
        </div>
      );
    }

    const { finalInvoice } = this.state;
    const surgeryBills = finalInvoice?.surgeryBillsSnapshot || [];

    return (
      <div style={{ 
        padding: "20px", 
        backgroundColor: "var(--bg-primary)", 
        color: "var(--text-primary)", 
        minHeight: "100vh" 
      }}>
        <div style={{ marginBottom: "20px" }}>
          <H1 style={{ color: "var(--text-primary)", margin: 0 }}>
            Final Invoice
          </H1>
          <p style={{ color: "var(--text-secondary)", margin: "5px 0" }}>
            Surgery ID: {this.props.match.params.sxId}
          </p>
        </div>

        {/* Invoice Header */}
        {this.renderInvoiceHeader()}

        {/* Surgery Bills */}
        <div>
          <H3 style={{ color: "var(--text-primary)", marginBottom: "15px" }}>
            Surgery Details ({surgeryBills.length} procedures)
          </H3>
          
          {surgeryBills.length === 0 ? (
            <NonIdealState
              icon="document"
              title="No Surgery Bills Found"
              description="No surgery procedures have been recorded for this invoice."
            />
          ) : (
            <div>
              {surgeryBills.map((bill, index) => this.renderSurgeryBill(bill, index))}
            </div>
          )}
        </div>

        {/* Overall Totals */}
        {this.renderOverallTotals()}

        {/* Print Actions */}
        <div style={{ marginTop: "30px", textAlign: "center" }}>
          <Button 
            intent="primary" 
            icon="print" 
            large
            onClick={() => window.print()}
            style={{ marginRight: "10px" }}
          >
            Print Invoice
          </Button>
          <Button 
            icon="arrow-left" 
            large
            onClick={() => window.history.back()}
          >
            Back to Billing
          </Button>
        </div>
      </div>
    );
  }
}

export default inject("authStore")(observer(withRouter(FinalInvoicePage)));
